import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInfoCircle, FaTimes, FaGlobe, FaEnvelope, FaUser, FaCode } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

const InfoCard: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, themeClasses } = useTheme();

  const appVersion = '1.0.0';
  const developerName = 'Pradap Pandiyan';
  const website = 'www.pradappandiyan.com';
  const email = 'pradapjackie@gmail.com';

  return (
    <>
      {/* Floating Info Icon */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.1, rotate: 15 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-8 z-50 p-4 rounded-full shadow-lg backdrop-blur-sm ${
          theme === 'slate' ? 'bg-teal-400/20 border-2 border-teal-400 text-teal-400' :
          theme === 'midnight' ? 'bg-sky-400/20 border-2 border-sky-400 text-sky-400' :
          theme === 'studio' ? 'bg-teal-700/20 border-2 border-teal-700 text-teal-700' :
          
          'bg-sky-400/20 border-2 border-sky-400 text-sky-400'
        } transition-all duration-300`}
      >
        <FaInfoCircle className="w-6 h-6" />
      </motion.button>

      {/* Info Card Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            />

            {/* Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[90%] max-w-md"
            >
              <div className={`${themeClasses.card} rounded-2xl shadow-2xl overflow-hidden border-2 ${themeClasses.border}/30`}>
                {/* Header with gradient */}
                <div className={`relative p-6 bg-gradient-to-br ${
                  theme === 'slate' ? 'from-teal-400/20 to-teal-500/20' :
                  theme === 'midnight' ? 'from-sky-400/20 to-zinc-800/20' :
                  theme === 'studio' ? 'from-teal-700/20 to-indigo-500/20' :
                  
                  'from-sky-400/20 to-teal-500/20'
                }`}>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(false)}
                    className={`absolute top-4 right-4 p-2 rounded-full ${themeClasses.text} hover:${themeClasses.bg} transition-colors`}
                  >
                    <FaTimes className="w-5 h-5" />
                  </motion.button>

                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="flex justify-center mb-4"
                  >
                    <div className={`p-4 rounded-full ${
                      theme === 'slate' ? 'bg-teal-400' :
                      theme === 'midnight' ? 'bg-sky-400' :
                      theme === 'studio' ? 'bg-teal-700' :
                      
                      'bg-sky-400'
                    } text-white`}>
                      <FaCode className="w-8 h-8" />
                    </div>
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`text-2xl font-bold text-center ${themeClasses.text}`}
                  >
                    Portfolio Info
                  </motion.h2>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  {/* Version */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex items-center space-x-3"
                  >
                    <div className={`p-2 rounded-lg ${
                      theme === 'slate' ? 'bg-teal-400/20 text-teal-400' :
                      theme === 'midnight' ? 'bg-sky-400/20 text-sky-400' :
                      theme === 'studio' ? 'bg-teal-700/20 text-teal-700' :
                      
                      'bg-sky-400/20 text-sky-400'
                    }`}>
                      <FaCode className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`text-xs ${themeClasses.textSoft}`}>Version</p>
                      <p className={`font-semibold ${themeClasses.text}`}>
                        {appVersion}
                      </p>
                    </div>
                  </motion.div>

                  {/* Developer */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center space-x-3"
                  >
                    <div className={`p-2 rounded-lg ${
                      theme === 'slate' ? 'bg-teal-400/20 text-teal-400' :
                      theme === 'midnight' ? 'bg-sky-400/20 text-sky-400' :
                      theme === 'studio' ? 'bg-teal-700/20 text-teal-700' :
                      
                      'bg-teal-400/20 text-teal-400'
                    }`}>
                      <FaUser className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`text-xs ${themeClasses.textSoft}`}>Developer</p>
                      <p className={`font-semibold ${themeClasses.text}`}>
                        {developerName}
                      </p>
                    </div>
                  </motion.div>

                  {/* Website */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="flex items-center space-x-3"
                  >
                    <div className={`p-2 rounded-lg ${
                      theme === 'slate' ? 'bg-teal-400/20 text-teal-400' :
                      theme === 'midnight' ? 'bg-sky-400/20 text-sky-400' :
                      theme === 'studio' ? 'bg-teal-700/20 text-teal-700' :
                      
                      'bg-sky-400/20 text-sky-400'
                    }`}>
                      <FaGlobe className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs ${themeClasses.textSoft}`}>Website</p>
                      <a
                        href={`https://${website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`font-semibold hover:underline ${
                          theme === 'slate' ? 'text-teal-400' :
                          theme === 'midnight' ? 'text-sky-400' :
                          theme === 'studio' ? 'text-teal-700' :
                          
                          'text-sky-400'
                        }`}
                      >
                        {website}
                      </a>
                    </div>
                  </motion.div>

                  {/* Email */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                    className="flex items-center space-x-3"
                  >
                    <div className={`p-2 rounded-lg ${
                      theme === 'slate' ? 'bg-teal-400/20 text-teal-400' :
                      theme === 'midnight' ? 'bg-sky-400/20 text-sky-400' :
                      theme === 'studio' ? 'bg-teal-700/20 text-teal-700' :
                      
                      'bg-teal-400/20 text-teal-400'
                    }`}>
                      <FaEnvelope className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className={`text-xs ${themeClasses.textSoft}`}>Email</p>
                      <a
                        href={`mailto:${email}`}
                        className={`font-semibold hover:underline ${
                          theme === 'slate' ? 'text-teal-400' :
                          theme === 'midnight' ? 'text-sky-400' :
                          theme === 'studio' ? 'text-teal-700' :
                          
                          'text-teal-400'
                        }`}
                      >
                        {email}
                      </a>
                    </div>
                  </motion.div>

                  {/* Footer */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className={`pt-4 mt-4 border-t ${themeClasses.border}/20 text-center`}
                  >
                    <p className={`text-xs ${themeClasses.textSoft}`}>
                      Built with React, TypeScript & Framer Motion
                    </p>
                    <p className={`text-xs ${themeClasses.textSoft} mt-1`}>
                      © 2024 {developerName}. All rights reserved.
                    </p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default InfoCard;
